import { QueryResult } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { getAuth0Config } from '../auth';
import { authedAtTimestampAtom, userTypeAtom } from '../auth/state';
import { LoadingScreen } from '../components/LoadingScreen';
import { PROVIDER_PATH } from '../config';
import { gracefullySetLoggerGlobalContext, logger } from '../datadog/logger';
import { ErrorPage } from '../error/ErrorPage';
import { useGetPublicProvider } from '../hooks/useGetPublicProvider';
import { useIsZoomEnabledCallback } from '../hooks/useIsZoomEnabled';
import { loadSegment } from '../segment/segment';
import { useGracefullyUpdateStatsigUserCallback } from '../statsig/useGracefullyUpdateStatsigUserCallback';
import { Provider, UserType } from '../types';
import { setQueryParams } from '../utils';
import { useInitializeVideoSDKAdapter } from '../video-sdk/actions/useLifecycleActions';
import { userDataAtom } from './state';
import { GetCurrentProviderQueryData, useGetCurrentProviderQueryCallback } from './useGetCurrentProviderQuery';

type ProviderProtectedProps = {
  auth0Config: ReturnType<typeof getAuth0Config>;
  children: React.ReactNode;
  getCurrentProvider: ReturnType<typeof useGetCurrentProviderQueryCallback>[0];
  isLoadingAuth: boolean;
  onAuthSuccess?: (provider: Required<Provider>) => void;
  hasAuthedBefore?: boolean;
  setUserType: (userType: UserType) => void;
} & Pick<ReturnType<typeof useAuth0>, 'isAuthenticated' | 'loginWithRedirect' | 'logout'>;

export function ProviderProtected({
  auth0Config: { loginUri },
  children,
  getCurrentProvider,
  isAuthenticated,
  isLoadingAuth,
  loginWithRedirect,
  logout,
  onAuthSuccess,
  hasAuthedBefore,
  setUserType,
}: ProviderProtectedProps) {
  const Loader = <LoadingScreen key='provider-protected-loader' />;
  const requiresLogin = !isLoadingAuth && !isAuthenticated;

  useEffect(
    function setUserTypeToProvider() {
      setUserType(UserType.PROVIDER);
    },
    [setUserType],
  );

  useEffect(
    function handleAuth() {
      if (isLoadingAuth || hasAuthedBefore) return;
      if (requiresLogin) {
        loginWithRedirect({
          appState: { returnTo: PROVIDER_PATH },
        });
        return;
      }
      (async function authorize() {
        let result: QueryResult<GetCurrentProviderQueryData> | null = null;
        let error: Error | null = null;
        try {
          // Getting provider manually to prevent the entire page from loading when the
          // Apollo context changes e.g. when a new access token is fetched.
          result = await getCurrentProvider();
        } catch (e) {
          error = e as Error;
        } finally {
          const provider = result?.data?.currentProvider;
          if (!provider?.shortId) {
            logger.warn('Unable to get current provider. Logging out.', {}, ...(error ? [error] : []));
            logout({
              logoutParams: {
                returnTo: setQueryParams(loginUri, {
                  error_description:
                    'Unable to find a Telehealth account for the user you logged in as. If you’re a client, please use the link provided to you in your confirmation email.',
                }),
              },
            });
          } else {
            logger.info('Authenticated successfully', {
              providerShortId: provider.shortId,
            });
            onAuthSuccess?.(provider);
          }
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasAuthedBefore, isLoadingAuth, requiresLogin],
  );

  // To minimize disruptions to the provider, don't show loader if the user had
  // authed successfully before i.e. if we already have user info
  if (hasAuthedBefore) return children;

  return Loader;
}

/**
 * Returns a callback that handles an authorized provider before the protected
 * app is rendered.
 */
function useHandleSuccessfulAuthCallback() {
  const gracefullyUpdateStatsigUser = useGracefullyUpdateStatsigUserCallback();
  const getPublicProvider = useGetPublicProvider();
  const setAuthedAt = useSetAtom(authedAtTimestampAtom);
  const setUserData = useSetAtom(userDataAtom);
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter();
  const checkZoomIsEnabled = useIsZoomEnabledCallback();
  const [error, setError] = useState<Error>();

  const handler = useCallback(
    async (provider: Required<Provider>) => {
      // initialize Statsig with a Provider's State of Operation to allow more
      // targeted rollout of Zoom vs. Twilio
      const { data: publicProvider } = await getPublicProvider(provider.shortId);

      await gracefullyUpdateStatsigUser({
        userID: provider.shortId,
        customIDs: {
          providerShortID: provider.shortId,
        },
        custom: {
          providerUsState: publicProvider?.state || 'UNKNOWN',
          userType: UserType.PROVIDER,
        },
      });
      gracefullySetLoggerGlobalContext({ providerShortId: provider.shortId });

      loadSegment(UserType.PROVIDER, provider.shortId);
      setUserData(provider);

      // Video SDK initialization error should be blocking
      await initializeVideoSDKAdapter(checkZoomIsEnabled() ? 'zoom' : 'twilio').catch((err: Error) => {
        setError(err);
      });

      // This will trigger the protected app to render
      setAuthedAt(Date.now());
    },
    [
      checkZoomIsEnabled,
      gracefullyUpdateStatsigUser,
      initializeVideoSDKAdapter,
      setAuthedAt,
      setUserData,
      getPublicProvider,
    ],
  );

  return [
    handler,
    {
      error,
    },
  ] as const;
}

export function ProviderProtectedWrapper({ children }: Pick<ProviderProtectedProps, 'children'>) {
  const [getCurrentProvider] = useGetCurrentProviderQueryCallback();
  const { isLoading: isLoadingAuth, logout, ...auth0Props } = useAuth0();
  const [handleSuccessfulAuth, { error }] = useHandleSuccessfulAuthCallback();
  const hasAuthedBefore = !!useAtomValue(authedAtTimestampAtom);
  const setUserType = useSetAtom(userTypeAtom);

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <ProviderProtected
      {...{
        auth0Config: getAuth0Config(),
        ...auth0Props,
        getCurrentProvider,
        isLoadingAuth,
        logout,
        onAuthSuccess: handleSuccessfulAuth,
        hasAuthedBefore,
        setUserType,
      }}
    >
      {children}
    </ProviderProtected>
  );
}
